import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, Check, Clock } from 'lucide-react'
import { devicesApi } from '../api/client'
import type { EnrollmentCode } from '../types'
import { formatDistanceToNow } from 'date-fns'

export default function Enroll() {
  const queryClient = useQueryClient()
  const [claimCode, setClaimCode] = useState('')
  const [claimError, setClaimError] = useState('')
  const [claimSuccess, setClaimSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['pending-enrollments'],
    queryFn: devicesApi.listPending,
    refetchInterval: 5000,
  })

  const claimMutation = useMutation({
    mutationFn: devicesApi.claim,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-enrollments'] })
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      setClaimCode('')
      setClaimError('')
      setClaimSuccess(true)
      setTimeout(() => setClaimSuccess(false), 3000)
    },
    onError: (error: Error) => {
      setClaimError(error.message || 'Failed to approve device')
    },
  })

  const enrollments: EnrollmentCode[] = data?.enrollments ?? []

  const handleManualClaim = (e: React.FormEvent) => {
    e.preventDefault()
    setClaimError('')
    if (claimCode.trim()) {
      claimMutation.mutate(claimCode.trim().toUpperCase())
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Enroll Devices</h1>

      {/* Manual code entry */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">
          Enter Enrollment Code
        </h2>
        <p className="text-slate-400 text-sm mb-4">
          Enter the code displayed on the device after running the installer.
        </p>

        <form onSubmit={handleManualClaim} className="flex gap-4">
          <input
            type="text"
            value={claimCode}
            onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX"
            className="input flex-1 uppercase font-mono tracking-wider text-center text-lg"
            maxLength={9}
          />
          <button
            type="submit"
            disabled={claimMutation.isPending || !claimCode.trim()}
            className="btn btn-primary"
          >
            {claimMutation.isPending ? 'Approving...' : 'Approve'}
          </button>
        </form>

        {claimError && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {claimError}
          </div>
        )}

        {claimSuccess && (
          <div className="mt-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400 text-sm flex items-center gap-2">
            <Check className="w-4 h-4" />
            Device approved successfully!
          </div>
        )}
      </div>

      {/* Pending enrollments list */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">
          Pending Enrollments
        </h2>

        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-700 rounded-lg" />
            ))}
          </div>
        ) : enrollments.length === 0 ? (
          <div className="text-center py-8">
            <UserPlus className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No pending enrollments</p>
            <p className="text-slate-500 text-sm mt-1">
              Run the installer on a new device to see it here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {enrollments.map((enrollment) => (
              <div
                key={enrollment.id}
                className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-yellow-500/10 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-lg text-white tracking-wider">
                        {enrollment.user_code}
                      </span>
                    </div>
                    <div className="text-slate-400 text-sm mt-1">
                      {(enrollment.device_info?.hostname as string) || 'Unknown host'} •
                      Expires{' '}
                      {formatDistanceToNow(new Date(enrollment.expires_at), {
                        addSuffix: true,
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => claimMutation.mutate(enrollment.user_code)}
                    disabled={claimMutation.isPending}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="card mt-8">
        <h2 className="text-lg font-semibold text-white mb-4">
          How to Enroll a Device
        </h2>
        <ol className="space-y-4 text-slate-300">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white text-sm flex items-center justify-center">
              1
            </span>
            <div>
              <p className="font-medium text-white">Install the agent</p>
              <p className="text-sm text-slate-400 mt-1">
                Run on your edge device:
                <code className="block mt-2 p-2 bg-slate-700 rounded text-xs">
                  curl -fsSL https://get.gatewarden.net | sudo bash
                </code>
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white text-sm flex items-center justify-center">
              2
            </span>
            <div>
              <p className="font-medium text-white">Copy the enrollment code</p>
              <p className="text-sm text-slate-400 mt-1">
                The installer will display a code like XXXX-XXXX
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white text-sm flex items-center justify-center">
              3
            </span>
            <div>
              <p className="font-medium text-white">Approve the device</p>
              <p className="text-sm text-slate-400 mt-1">
                Enter the code above or click Approve on the pending enrollment
              </p>
            </div>
          </li>
        </ol>
      </div>
    </div>
  )
}
